// src/data/wordForms.js
// Welche Formen eines Vokabel-Worts in Sätzen als bekannt gelten (Geschlecht, Mehrzahl,
// Kurzformen). Genutzt von der App (knownWordsBuilder.js, sentencePool.js) und vom
// Prüfskript scripts/vocab-pipeline/check-cloze-sentences.js, damit beide gleich rechnen.
// Die Datei darf deshalb nichts importieren (das Skript wertet sie direkt aus).
//
// Regeln je Lernsprache: Spanisch (Standard) und Französisch (`lang` = "fr"). Kommt eine
// weitere Lernsprache dazu, braucht sie hier eigene Regeln (siehe Leitfaden „Sätze schreiben“
// in CLAUDE.md).

// Kurzformen vor Substantiven (buen día, algún momento, gran ciudad)
const SHORT_FORMS = {
  bueno: "buen",
  malo: "mal",
  grande: "gran",
  primero: "primer",
  tercero: "tercer",
  alguno: "algún",
  ninguno: "ningún",
  cualquiera: "cualquier",
  santo: "san",
};

// Formen, die sich nicht aus -o → -a und der Mehrzahl ergeben
const IRREGULAR_FORMS = {
  este: ["esta", "estos", "estas"],
  ese: ["esa", "esos", "esas"],
  aquel: ["aquella", "aquellos", "aquellas"],
};

// Bei Adjektiven hat ein -o am Ende immer auch die weibliche Form (rojo → roja).
// Funktionswörter, Adverbien und Themen-Wörter nur, wenn sie hier stehen (todo → toda,
// mucho → mucha), sonst entstünden falsche oder fremde Wörter (como → coma, algo → alga).
// Bei Substantiven nie (libro ≠ libra).
const GENDERED_WORDS = [
  "todo", "mucho", "poco", "otro", "mismo", "tanto", "cuanto", "cuánto", "demasiado",
  "solo", "junto", "uno", "alguno", "ninguno", "nuestro", "vuestro", "cierto", "propio",
  "primero", "segundo", "tercero", "último", "medio", "cuyo",
];

/** Mehrzahl: vez → veces, casa → casas, ciudad → ciudades */
function pluralForms(word) {
  if (word.endsWith("z")) return [word.slice(0, -1) + "ces"];
  return [word + "s", word + "es"];
}

// --- Französisch ---------------------------------------------------------------------------

// Formen, die sich nicht aus den Regeln unten ergeben (Kurzformen vor Vokal: bel ami, cet homme)
const FRENCH_IRREGULAR_FORMS = {
  beau: ["bel", "belle", "beaux", "belles"],
  nouveau: ["nouvel", "nouvelle", "nouveaux", "nouvelles"],
  vieux: ["vieil", "vieille", "vieilles"],
  fou: ["fol", "folle", "fous", "folles"],
  ce: ["cet", "cette", "ces"],
  mon: ["ma", "mes"],
  ton: ["ta", "tes"],
  son: ["sa", "ses"],
  notre: ["nos"],
  votre: ["vos"],
  leur: ["leurs"],
  le: ["la", "les", "l'"],
  un: ["une"],
  au: ["aux"],
  tout: ["toute", "tous", "toutes"],
  quel: ["quelle", "quels", "quelles"],
  lequel: ["laquelle", "lesquels", "lesquelles"],
  aucun: ["aucune"],
  chacun: ["chacune"],
  certain: ["certaine", "certains", "certaines"],
  tel: ["telle", "tels", "telles"],
  nul: ["nulle"],
  quelque: ["quelques"],
  autre: ["autres"],
  mien: ["mienne", "miens", "miennes"],
  tien: ["tienne", "tiens", "tiennes"],
  sien: ["sienne", "siens", "siennes"],
  même: ["mêmes"],
};
// Mehrzahl auf -oux bzw. -aux statt -s
const FRENCH_PLURAL_OUX = ["bijou", "caillou", "chou", "genou", "hibou", "joujou", "pou"];
const FRENCH_PLURAL_AIL_AUX = ["travail", "vitrail", "corail", "émail", "bail", "soupirail"];
// verkürzte Wörter vor Vokal: l'eau, d'accord, j'ai, qu'il, jusqu'à
const FRENCH_ELISION = /^(l|d|j|m|t|s|c|n|qu|jusqu|lorsqu|puisqu|quoiqu)'(.+)$/;

/** Französische Mehrzahl: maison → maisons, journal → journaux, bateau → bateaux, prix → prix */
function frenchPluralForms(word) {
  if (/[sxz]$/.test(word)) return [word];
  if (/(eau|au)$/.test(word)) return [word + "x"];
  // jeu → jeux, bleu → bleus: beide zulassen
  if (word.endsWith("eu")) return [word + "x", word + "s"];
  // journal → journaux, aber festival → festivals: beide zulassen
  if (word.endsWith("al")) return [word.slice(0, -2) + "aux", word + "s"];
  if (FRENCH_PLURAL_OUX.includes(word)) return [word + "x"];
  if (FRENCH_PLURAL_AIL_AUX.includes(word)) return [word.slice(0, -3) + "aux"];
  return [word + "s"];
}

/**
 * Weibliche Form aus der Schreibweise der Vokabeln: Endung ("petit/e" → petite, "bon/ne" →
 * bonne) oder ganzes Wort ("heureux/heureuse", "premier/première", "beau/belle").
 */
function frenchFeminine(base, fem) {
  return fem.length >= 3 && fem[0] === base[0] ? fem : base + fem;
}

function expandFrenchWordForms(word, type) {
  const text = String(word).replace(/’/g, "'");
  const [base, fem] = text.split("/");
  if (!base) return [];
  if (type === "verb") return [base];

  const forms = new Set([base]);
  // l'eau → auch "eau" (Satz-Wörter werden nach dem Apostroph getrennt verglichen)
  const elided = base.match(FRENCH_ELISION);
  const stem = elided ? elided[2] : base;
  forms.add(stem);
  // "le tien/la tienne": hinter dem Schrägstrich steht der Artikel, die Form kommt als eigenes Wort
  if (fem && !["la", "le", "les"].includes(fem)) forms.add(frenchFeminine(stem, fem));
  // Adverbien und Funktionswörter: Mehrzahl nur aus FRENCH_IRREGULAR_FORMS (tous, autres)
  if (type !== "adverb" && type !== "other") {
    [...forms].forEach((f) => {
      if (f !== base || !elided) frenchPluralForms(f).forEach((p) => forms.add(p));
    });
  }
  (FRENCH_IRREGULAR_FORMS[stem] || []).forEach((f) => forms.add(f));
  return [...forms];
}

/**
 * Formen eines Vokabel-Worts (klein geschrieben, ohne Satzzeichen):
 * "nuevo/a" → nuevo, nueva, nuevos, nuevas; "todo" (other) → todo, toda, todos, todas;
 * "bueno/a" → … und buen. Verben bleiben unverändert (ihre Formen kommen aus den
 * Konjugationsdateien). Französisch: "petit/e" → petit, petite, petits, petites;
 * "l'eau" → l'eau, eau, eaux; "beau/belle" → … und bel.
 */
export function expandWordForms(word, type, lang = "es") {
  if (lang === "fr") return expandFrenchWordForms(word, type);
  const [base, fem] = String(word).split("/");
  if (!base) return [];
  if (type === "verb") return [base];

  const forms = new Set([base]);
  if (fem) forms.add(base.endsWith("o") ? base.slice(0, -1) + fem : base + fem);
  else if (base.endsWith("o") && (type === "adjektiv" || GENDERED_WORDS.includes(base)))
    forms.add(base.slice(0, -1) + "a");

  [...forms].forEach((f) => pluralForms(f).forEach((p) => forms.add(p)));
  if (SHORT_FORMS[base]) forms.add(SHORT_FORMS[base]);
  (IRREGULAR_FORMS[base] || []).forEach((f) => forms.add(f));
  return [...forms];
}
